import { createContext, type ReactNode, useState } from "react";

import { Dialog } from "../Dialog";

/**
 * Portal target for floating editors rendered inside this dialog. The Radix
 * modal dialog traps focus and pointer events inside its content and paints
 * its overlay at z-120, so a popover portalled to `document.body` (z-50) is
 * painted under the overlay and its focus is stolen back by the dialog.
 * `EditPopover` portals into this element instead when it renders in here.
 *
 * Value semantics follow floating-ui's `FloatingPortal` root prop: `undefined`
 * (the default, outside any dialog) portals to `document.body`; `null` (dialog
 * mounted, content ref pending) tells the portal to wait for the element.
 */
export const PhonePreviewDialogPortalContext = createContext<HTMLElement | null | undefined>(
  undefined,
);

type PhonePreviewDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: ReactNode;
};

export const PhonePreviewDialog = ({ open, onClose, title, children }: PhonePreviewDialogProps) => {
  const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={<h2 className="text-center type-label-lg">{title}</h2>}
      contentRef={setPortalRoot}
      contentClassName="w-auto max-h-[90vh] items-center"
      manageContentScroll={false}
    >
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <PhonePreviewDialogPortalContext.Provider value={portalRoot}>
          {children}
        </PhonePreviewDialogPortalContext.Provider>
      </div>
    </Dialog>
  );
};
